import { X, SearchX } from "lucide-react";

import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../../store";
import { useCallback, useEffect, useState } from "react";
import { fetchStockReductionLogs } from "../../../store/slices/warehouseSlice";
import SearchInput from "../../../components/Search/SearchInput";
import PaginationControls from "../../../components/pagination/PaginationControls";
import { formatToDDMMYYYY } from "../../../utils/formatDateUtils";

interface Props {
  open: boolean;
  onClose: () => void;
  rawmaterialId?: string | undefined;
  productId?: string | undefined;
}

export default function StockReductionHistoryDrawer({
  open,
  onClose,
  rawmaterialId,
  productId,
}: Props) {
  const dispatch = useDispatch<RootDispatch>();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const limit = 10;

  const fetchLogs = useCallback(async () => {
    await dispatch(
      fetchStockReductionLogs({
        page: 1,
        limit,
        search,
        rawMaterialId: rawmaterialId,
        productId,
      }),
    );
  }, [dispatch, limit, search, rawmaterialId, productId]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const logs = useSelector(
    (state: RootState) => state.warehouse.stockReductionLogs,
  );

  const pagination = useSelector(
    (state: RootState) => state.warehouse.stockReductionPagination,
  );

  const loading = useSelector(
    (state: RootState) => state.warehouse.stockReductionLoading,
  );

  const first = logs[0];

  const itemName =
    first?.product?.productName ?? first?.rawMaterial?.name ?? "-";

  const sku = first?.product?.sku ?? first?.rawMaterial?.sku ?? "-";
  const isMaterial = first?.itemType === "material";
  const totalReduced = logs?.reduce((sum, log) => sum + log.reducedQuantity, 0);
  const [isNextLoading, setIsNextLoading] = useState(false);
  const [isPrevLoading, setIsPrevLoading] = useState(false);

  const [selectedSerials, setSelectedSerials] = useState<string[]>([]);
  const [showSerialModal, setShowSerialModal] = useState(false);

  const handleNextPage = async () => {
    if (page >= (pagination?.totalPages || 1)) return;
    setIsNextLoading(true);
    try {
      const nextPage = page + 1;
      await dispatch(
        fetchStockReductionLogs({
          page: nextPage,
          limit,
          search,
          rawMaterialId: rawmaterialId,
          productId,
        }),
      );
      setPage(nextPage);
    } catch (error) {
      console.error("Error fetching next page:", error);
    } finally {
      setIsNextLoading(false);
    }
  };

  const handlePreviousPage = async () => {
    if (page <= 1) return;
    setIsPrevLoading(true);
    try {
      const prevPage = page - 1;
      await dispatch(
        fetchStockReductionLogs({
          page: prevPage,
          limit,
          search,
          rawMaterialId: rawmaterialId,
          productId,
        }),
      );
      setPage(prevPage);
    } catch (error) {
      console.error("Error fetching previous page:", error);
    } finally {
      setIsPrevLoading(false);
    }
  };

  if (!open) return null;
  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-opacity"
        onClick={onClose}
      />

      <div className="fixed inset-0 z-[110] flex sm:items-center sm:justify-center p-0 sm:p-4">
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm sm:hidden"
          onClick={onClose}
        />

        <div className="relative w-full sm:w-[95vw] lg:w-[90vw] h-[100dvh] sm:h-[92vh] bg-gradient-to-b from-white to-gray-50 sm:rounded-2xl shadow-2xl flex flex-col z-10 overflow-hidden">
          {/* Header */}
          <div className="w-full flex-none border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-5 bg-white">
            {/* Top row */}
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-lg sm:text-xl font-bold leading-tight truncate text-gray-900">
                Stock Reduction History
              </h2>

              <button
                onClick={onClose}
                className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 transition-colors shrink-0"
                aria-label="Close drawer"
              >
                <X size={20} className="text-gray-600" />
              </button>
            </div>

            {/* Meta info - Modern card style */}
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                  Item
                </p>
                <p className="mt-1 text-sm font-semibold text-gray-900 line-clamp-2">
                  {itemName}
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                  SKU
                </p>
                <p className="mt-1 text-sm font-mono font-semibold text-gray-900">
                  {sku}
                </p>
              </div>

              <div className="bg-red-50 rounded-lg p-3 col-span-2 sm:col-span-2">
                <p className="text-xs text-red-600 font-medium uppercase tracking-wide">
                  Total Reduced for {limit} logs
                </p>
                <p className="mt-1 text-xl sm:text-2xl font-bold text-red-600">
                  {totalReduced}
                </p>
              </div>
            </div>

            {/* Search - Modern input */}
            <div className="mt-4">
              <SearchInput
                value={search}
                placeholder="Search order, customer, mobile..."
                className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none shadow-sm transition-all"
                onSearch={(value) => {
                  setSearch(value);
                  setPage(1);
                }}
              />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-auto">
            {loading ? (
              <div className="flex justify-center items-center h-full">
                <div className="flex flex-col items-center gap-3">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
                  <p className="text-gray-600 text-sm">Loading logs...</p>
                </div>
              </div>
            ) : logs.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full">
                <div className="rounded-full bg-gray-100 p-4">
                  <SearchX size={40} className="text-gray-400" />
                </div>
                <p className="mt-4 text-gray-600 font-medium">
                  No Stock Reduction Logs Found
                </p>
              </div>
            ) : (
              <div className="w-full">
                {/* Desktop: Table view */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-gray-100 border-b border-gray-200">
                      <tr>
                        <th className="text-left px-4 py-3 font-semibold text-gray-700">
                          Order
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-700">
                          Qty
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-700">
                          Date
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-700">
                          Customer
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-700">
                          Mobile
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-700">
                          Warehouse
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-700">
                          Dispatched By
                        </th>
                        {!isMaterial && (
                          <th className="text-center px-4 py-3 font-semibold text-gray-700">
                            Serials
                          </th>
                        )}
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-200">
                      {logs.map((log) => {
                        const customer =
                          log.customer?.name ??
                          log.whatsappCustomer?.customerName ??
                          "-";

                        const mobile =
                          log.customer?.mobileNumber ??
                          log.whatsappCustomer?.mobileNumber ??
                          "-";

                        return (
                          <tr
                            key={log._id}
                            className="hover:bg-gray-50 transition-colors"
                          >
                            <td className="px-4 py-3 font-medium text-gray-900">
                              {log.order?.orderNumber || "-"}
                            </td>

                            <td className="px-4 py-3 font-semibold text-gray-900">
                              {log.reducedQuantity}{" "}
                              <span className="font-normal text-gray-600">
                                {log.rawMaterial?.unit || "piece"}
                              </span>
                            </td>

                            <td className="px-4 py-3 text-gray-700">
                              {formatToDDMMYYYY(log.dispatchDate)}
                            </td>

                            <td className="px-4 py-3 text-gray-700">
                              {customer}
                            </td>

                            <td className="px-4 py-3 text-gray-700 font-mono">
                              {mobile}
                            </td>

                            <td className="px-4 py-3 text-gray-700">
                              {log.warehouse?.warehouseName || "-"}
                            </td>

                            <td className="px-4 py-3 text-gray-700">
                              {log.reducedBy?.username || "-"}
                            </td>

                            {!isMaterial && (
                              <td className="px-4 py-3 text-center">
                                {log.serialNumbers.length ? (
                                  <button
                                    className="inline-flex items-center justify-center px-3 py-1 text-sm font-semibold text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
                                    onClick={() => {
                                      setSelectedSerials(log.serialNumbers);
                                      setShowSerialModal(true);
                                    }}
                                  >
                                    {log.serialNumbers.length}
                                  </button>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile: Card view */}
                <div className="md:hidden space-y-3 p-4">
                  {logs.map((log) => {
                    const customer =
                      log.customer?.name ??
                      log.whatsappCustomer?.customerName ??
                      "-";

                    const mobile =
                      log.customer?.mobileNumber ??
                      log.whatsappCustomer?.mobileNumber ??
                      "-";

                    return (
                      <div
                        key={log._id}
                        className="bg-white rounded-lg border border-gray-200 p-4 space-y-3 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-xs text-gray-500 font-medium uppercase">
                              Order Number
                            </p>
                            <p className="text-base font-bold text-gray-900">
                              {log.order?.orderNumber || "-"}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-gray-500 font-medium uppercase">
                              Quantity Reduced
                            </p>
                            <p className="text-base font-bold text-red-600">
                              {log.reducedQuantity}{" "}
                              <span className="text-xs font-normal text-gray-600">
                                {log.rawMaterial?.unit || "pc"}
                              </span>
                            </p>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div className="bg-gray-50 rounded p-2">
                            <p className="text-xs text-gray-500 font-medium uppercase">
                              Date
                            </p>
                            <p className="mt-1 font-medium text-gray-900">
                              {formatToDDMMYYYY(log.dispatchDate)}
                            </p>
                          </div>
                          <div className="bg-gray-50 rounded p-2">
                            <p className="text-xs text-gray-500 font-medium uppercase">
                              Warehouse
                            </p>
                            <p className="mt-1 font-medium text-gray-900 truncate">
                              {log.warehouse?.warehouseName || "-"}
                            </p>
                          </div>
                        </div>

                        <div className="bg-blue-50 rounded p-2">
                          <p className="text-xs text-gray-500 font-medium uppercase">
                            Customer
                          </p>
                          <p className="mt-1 font-medium text-gray-900">
                            {customer}
                          </p>
                          <p className="text-sm text-gray-600 font-mono">
                            {mobile}
                          </p>
                        </div>

                        <div className="flex items-center justify-between gap-2 text-sm">
                          <div>
                            <p className="text-xs text-gray-500 font-medium uppercase">
                              Dispatched By
                            </p>
                            <p className="mt-1 font-medium text-gray-900">
                              {log.reducedBy?.username || "-"}
                            </p>
                          </div>
                          {!isMaterial && log.serialNumbers.length > 0 && (
                            <button
                              onClick={() => {
                                setSelectedSerials(log.serialNumbers);
                                setShowSerialModal(true);
                              }}
                              className="inline-flex items-center justify-center px-3 py-2 text-sm font-semibold text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
                            >
                              {log.serialNumbers.length} Serials
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Pagination */}
          {pagination?.totalPages && pagination?.totalPages > 1 && (
            <div className="border-t border-gray-200 p-4 bg-white flex justify-center">
              <PaginationControls
                currentPage={page}
                totalPages={pagination.totalPages || 0}
                isNextFetching={isNextLoading}
                isPrevFetching={isPrevLoading}
                onNext={handleNextPage}
                onPrev={handlePreviousPage}
              />
            </div>
          )}
        </div>
      </div>

      {/* Serial Modal */}
      {showSerialModal && (
        <>
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[120] transition-opacity"
            onClick={() => setShowSerialModal(false)}
          />

          <div className="fixed inset-0 flex items-end sm:items-center justify-center z-[121] p-4">
            <div className="w-full sm:w-[450px] max-h-[85vh] sm:max-h-[75vh] bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-bottom-5">
              <div className="border-b border-gray-200 px-5 py-4 flex items-center justify-between bg-gradient-to-r from-gray-50 to-white">
                <h3 className="font-bold text-gray-900">
                  Serial Numbers ({selectedSerials.length})
                </h3>

                <button
                  onClick={() => setShowSerialModal(false)}
                  className="flex items-center justify-center w-9 h-9 rounded-full hover:bg-gray-200 transition-colors"
                  aria-label="Close modal"
                >
                  <X size={18} className="text-gray-600" />
                </button>
              </div>

              <div className="overflow-auto flex-1 p-5">
                <div className="grid gap-3">
                  {selectedSerials.map((serial, idx) => (
                    <div
                      key={serial}
                      className="bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-lg px-4 py-3 font-mono text-sm text-gray-900 flex items-center justify-between group hover:border-gray-300 transition-colors"
                    >
                      <span>
                        <span className="text-xs text-gray-500">
                          #{idx + 1}
                        </span>{" "}
                        {serial}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
